import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import "./App.css";
import { API_BASE_URL } from "./apiConfig";

const SERVICES_DATA = [
  {
    icon: "fa-bolt",
    title: "Express Delivery",
    desc: "Same-day and next-day delivery for urgent shipments, backed by real-time dispatch tracking.",
  },
  {
    icon: "fa-truck-fast",
    title: "Domestic Shipping",
    desc: "Reliable door-to-door courier service covering every city and pin code in the country.",
  },
  {
    icon: "fa-globe",
    title: "International Shipping",
    desc: "Customs-cleared global freight and courier solutions to 100+ countries worldwide.",
  },
  {
    icon: "fa-warehouse",
    title: "Warehousing & Fulfilment",
    desc: "Secure storage, inventory management, and pick-pack-ship fulfilment for businesses.",
  },
  {
    icon: "fa-box",
    title: "Bulk & Corporate Shipping",
    desc: "Tailored logistics contracts, discounted rates, and dedicated account managers for enterprises.",
  },
  {
    icon: "fa-route",
    title: "Real-Time Tracking",
    desc: "Track every shipment live from pickup to delivery with instant status notifications.",
  },
];

const STEPS_DATA = [
  {
    icon: "fa-box-open",
    title: "Book",
    desc: "Schedule a pickup online or drop off at your nearest branch.",
  },
  {
    icon: "fa-dolly",
    title: "Pickup",
    desc: "Our courier collects and scans your package at your doorstep.",
  },
  {
    icon: "fa-route",
    title: "In Transit",
    desc: "Track live as your shipment moves through our network.",
  },
  {
    icon: "fa-house-circle-check",
    title: "Delivered",
    desc: "Signed, sealed, delivered — with instant confirmation.",
  },
];

const TESTIMONIALS_DATA = [
  {
    initial: "A",
    name: "Alex Johnson",
    program: "E-commerce Seller",
    text: "“Their express delivery cut our shipping times in half. Tracking is accurate and support is always responsive.”",
  },
  {
    initial: "S",
    name: "Sophia Lee",
    program: "Retail Chain Owner",
    text: "“We moved all our bulk shipping to them. The corporate rates and dedicated account manager made a huge difference.”",
  },
  {
    initial: "M",
    name: "Mohammed Khan",
    program: "Import/Export Business",
    text: "“International shipments used to be a headache. Now customs clearance and delivery are seamless.”",
  },
  {
    initial: "R",
    name: "Rachel Green",
    program: "Small Business Owner",
    text: "“Real-time tracking means my customers always know where their package is. It's boosted my repeat orders.”",
  },
];

const FAQS_DATA = [
  {
    q: "How do I track my shipment?",
    a: "Enter your AWB / tracking number in the 'Track Your Shipment' box on this page, or use the Portal Login for full shipment history.",
  },
  {
    q: "What areas do you deliver to?",
    a: "We deliver across all domestic locations and to over 100 countries internationally via our partner network.",
  },
  {
    q: "How long does delivery take?",
    a: "Express domestic delivery is typically same-day or next-day. International shipments vary by destination and customs processing.",
  },
  {
    q: "Do you offer bulk/corporate shipping rates?",
    a: "Yes, we offer customised contracts, discounted rates, and a dedicated account manager for corporate and bulk shippers.",
  },
  {
    q: "What if my parcel is damaged?",
    a: "Contact our support team within 48 hours of delivery with photos of the damage and your AWB number for a claim.",
  },
  {
    q: "How can I contact support?",
    a: "Use the Contact Us form below, call our branch, or reach out via our social media pages.",
  },
];

const GALLERY_IMAGES = [
  "/images/gallery/1.jpg",
  "/images/gallery/2.jpg",
  "/images/gallery/3.jpg",
  "/images/gallery/4.jpg",
];

const COURIER_REDIRECT_URL = "#";

const ImageCarousel = ({ images }) => {
  const trackRef = useRef(null);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    let raf;
    const step = () => {
      if (!paused && el) {
        el.scrollLeft += 1;
        if (el.scrollLeft >= el.scrollWidth / 2) el.scrollLeft = 0;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [paused]);

  const loopImages = [...images, ...images];

  return (
    <div
      className="image-carousel-track"
      ref={trackRef}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{
        display: "flex",
        overflowX: "hidden",
        gap: "16px",
        scrollBehavior: "auto",
      }}
    >
      {loopImages.map((src, i) => (
        <img
          key={i}
          src={src}
          alt={`Gallery ${i + 1}`}
          style={{
            height: "220px",
            width: "320px",
            objectFit: "cover",
            borderRadius: "10px",
            flex: "0 0 auto",
          }}
        />
      ))}
    </div>
  );
};

const Home = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [activeFaq, setActiveFaq] = useState(null);

  const [trackingNumber, setTrackingNumber] = useState("");
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingResult, setTrackingResult] = useState(null);
  const [trackingError, setTrackingError] = useState("");

  const [contactForm, setContactForm] = useState({
    Name: "",
    Email: "",
    Phone: "",
    Message: "",
  });
  const [contactStatus, setContactStatus] = useState("");
  const [contactLoading, setContactLoading] = useState(false);

  const statsRef = useRef(null);
  const [countersTriggered, setCountersTriggered] = useState(false);
  const [stats, setStats] = useState({
    shipments: 0,
    cities: 0,
    clients: 0,
    satisfaction: 0,
  });

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  const handleScroll = useCallback(() => {
    setShowScrollTop(window.scrollY > 300);
    const header = document.getElementById("header");
    if (header) {
      if (window.scrollY > 50) header.classList.add("scrolled");
      else header.classList.remove("scrolled");
    }
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    const currentRef = statsRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !countersTriggered) {
          setCountersTriggered(true);
          animateCounter("shipments", 50000);
          animateCounter("cities", 500);
          animateCounter("clients", 1200);
          animateCounter("satisfaction", 98);
        }
      },
      { threshold: 0.5 },
    );
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [countersTriggered]);

  const animateCounter = (key, target) => {
    let current = 0;
    const increment = target / 50;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setStats((prev) => ({ ...prev, [key]: target }));
        clearInterval(timer);
      } else {
        setStats((prev) => ({ ...prev, [key]: Math.ceil(current) }));
      }
    }, 30);
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const toggleTheme = () => setIsDarkMode((prev) => !prev);
  const toggleMenu = () => setIsNavOpen((prev) => !prev);
  const toggleFaq = (index) => setActiveFaq(activeFaq === index ? null : index);

  const nextTestimonial = () =>
    setActiveTestimonial((prev) => (prev + 1) % TESTIMONIALS_DATA.length);
  const prevTestimonial = () =>
    setActiveTestimonial(
      (prev) =>
        (prev - 1 + TESTIMONIALS_DATA.length) % TESTIMONIALS_DATA.length,
    );

  const handleTrackSubmit = async (e) => {
    e.preventDefault();
    const query = trackingNumber.trim();
    if (!query) {
      setTrackingError("Please enter a tracking / AWB number.");
      setTrackingResult(null);
      return;
    }

    setTrackingLoading(true);
    setTrackingError("");
    setTrackingResult(null);

    try {
      const res = await fetch(
        `${API_BASE_URL}/awb/track/${encodeURIComponent(query)}`,
      );
      if (res.status === 404) {
        setTrackingError(
          `No shipment found for "${query}". Please check the number and try again.`,
        );
        return;
      }
      if (!res.ok) throw new Error("Unable to reach tracking service.");
      const data = await res.json();
      setTrackingResult(data);
    } catch (err) {
      setTrackingError(
        "Something went wrong while tracking. Please try again.",
      );
    } finally {
      setTrackingLoading(false);
    }
  };

  const handleContactChange = (e) => {
    setContactForm({ ...contactForm, [e.target.name]: e.target.value });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    setContactStatus("");
    try {
      const res = await fetch(`${API_BASE_URL}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(contactForm),
      });
      if (!res.ok) throw new Error("Failed");
      setContactStatus("success");
      setContactForm({ Name: "", Email: "", Phone: "", Message: "" });
    } catch (err) {
      setContactStatus("error");
    } finally {
      setContactLoading(false);
    }
  };

  return (
    <div
      className={`app-container courier-home ${isDarkMode ? "dark-mode" : ""}`}
    >
      {isLoading && (
        <div id="preloader">
          <div className="loader-circle"></div>
          <p>Preparing your dashboard...</p>
        </div>
      )}

      <button
        id="scrollTopBtn"
        className={showScrollTop ? "show" : ""}
        onClick={scrollToTop}
        aria-label="Scroll to top"
      >
        <i className="fas fa-arrow-up"></i>
      </button>

      <button
        id="themeToggle"
        onClick={toggleTheme}
        aria-label="Toggle dark mode"
      >
        <i className={isDarkMode ? "fas fa-sun" : "fas fa-moon"}></i>
      </button>

      <header className="header" id="header">
        <nav className="navbar container">
          <div className="nav-logo">
            <span className="logo-icon">
              <i className="fas fa-truck-fast"></i>
            </span>
            <span className="logo-text">
              Combined<span>Courier, Ahmedabad</span>
            </span>
          </div>
          <div
            className={`nav-toggle ${isNavOpen ? "open" : ""}`}
            onClick={toggleMenu}
          >
            <span></span>
            <span></span>
            <span></span>
          </div>
          <ul className={`nav-menu ${isNavOpen ? "active" : ""}`}>
            <li>
              <a href="#track" className="nav-link" onClick={toggleMenu}>
                Track Order
              </a>
            </li>
            <li>
              <a href="#hero" className="nav-link" onClick={toggleMenu}>
                Home
              </a>
            </li>
            <li>
              <a href="#services" className="nav-link" onClick={toggleMenu}>
                Services
              </a>
            </li>
            <li>
              <a href="#about" className="nav-link" onClick={toggleMenu}>
                About Us
              </a>
            </li>
            <li>
              <a href="#gallery" className="nav-link" onClick={toggleMenu}>
                Gallery
              </a>
            </li>
            <li>
              <a href="#contact" className="nav-link" onClick={toggleMenu}>
                Contact
              </a>
            </li>
            <li>
              <Link to="/login" className="nav-link" onClick={toggleMenu}>
                Portal Login
              </Link>
            </li>
          </ul>
        </nav>
      </header>

      <main>
        <section className="section tracking-section" id="track">
          <div className="container">
            <div className="cargo-ticket">
              <div className="cargo-ticket-notch cargo-ticket-notch-left"></div>
              <div className="cargo-ticket-notch cargo-ticket-notch-right"></div>
              <h2>
                Track Your <span className="accent">Shipment</span>
              </h2>
              <p>Enter your AWB / tracking number to get live status.</p>
              <form className="tracking-form" onSubmit={handleTrackSubmit}>
                <input
                  type="text"
                  className="tracking-input"
                  placeholder="Enter AWB / Tracking Number (e.g. AWB123456)"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  aria-label="Tracking number"
                />
                <button
                  type="submit"
                  className="btn btn-primary tracking-btn"
                  disabled={trackingLoading}
                >
                  {trackingLoading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> Tracking...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-search"></i> Track
                    </>
                  )}
                </button>
              </form>

              {trackingError && (
                <div className="tracking-error">
                  <i className="fas fa-circle-exclamation"></i> {trackingError}
                </div>
              )}

              {trackingResult && (
                <div className="tracking-result">
                  <div className="tracking-result-header">
                    <i className="fas fa-box-circle-check"></i>
                    <h3>Shipment Found</h3>
                  </div>
                  <div className="tracking-result-grid">
                    <div className="tracking-result-item">
                      <span className="label">AWB Number</span>
                      <span className="value mono">
                        {trackingResult.awB_NO}
                      </span>
                    </div>
                    <div className="tracking-result-item">
                      <span className="label">Booking Date</span>
                      <span className="value">
                        {trackingResult.AWB_Dt
                          ? new Date(trackingResult.awB_Dt).toLocaleDateString()
                          : "-"}
                      </span>
                    </div>
                    <div className="tracking-result-item">
                      <span className="label">Quantity</span>
                      <span className="value">{trackingResult.qty ?? "-"}</span>
                    </div>
                    <div className="tracking-result-item">
                      <span className="label">Weight</span>
                      <span className="value">
                        {trackingResult.weight ?? "-"} kg
                      </span>
                    </div>
                    <div className="tracking-result-item full-width">
                      <span className="label">Remarks / Status</span>
                      <span className="value">
                        {trackingResult.remarks || "In Process"}
                      </span>
                    </div>
                  </div>
                  <a
                    href={COURIER_REDIRECT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline"
                    style={{ marginTop: "12px", display: "inline-block" }}
                  >
                    Track on Courier Partner{" "}
                    <i className="fas fa-arrow-up-right-from-square"></i>
                  </a>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="hero" id="hero">
          <div className="hero-overlay"></div>
          <div className="hero-content container">
            <div className="hero-text">
              <p className="hero-tagline">
                Fast · Reliable · Nationwide & Global
              </p>
              <h1>
                Every Package,{" "}
                <span className="accent">Tracked Door to Door</span>
              </h1>
              <p className="hero-subtitle">
                From same-day local drops to international freight, we move your
                shipments safely and on time — every time.
              </p>
              <div className="hero-cta">
                <a href="#track" className="btn btn-primary">
                  Track Your Order
                </a>
                <a href="#services" className="btn btn-outline">
                  Our Services
                </a>
              </div>
              <div className="hero-stats" ref={statsRef}>
                <div className="stat">
                  <span className="counter">{stats.shipments}</span>
                  <span>+</span>
                  <p>Shipments Delivered</p>
                </div>
                <div className="stat">
                  <span className="counter">{stats.cities}</span>
                  <span>+</span>
                  <p>Cities Covered</p>
                </div>
                <div className="stat">
                  <span className="counter">{stats.clients}</span>
                  <span>+</span>
                  <p>Corporate Clients</p>
                </div>
                <div className="stat">
                  <span className="counter">{stats.satisfaction}</span>
                  <span>%</span>
                  <p>Customer Satisfaction</p>
                </div>
              </div>
            </div>

            <div className="hero-visual">
              <div className="cargo-tag">
                <div className="cargo-tag-hole"></div>
                <div className="cargo-tag-stamp">EXPRESS</div>
                <span className="cargo-tag-label">AWB NO.</span>
                <p className="cargo-tag-number">SC-88231047</p>
                <div className="cargo-tag-barcode"></div>
                <div className="cargo-tag-row">
                  <div>
                    <span className="cargo-tag-label">FROM</span>
                    <p>Mumbai, IN</p>
                  </div>
                  <i className="fas fa-arrow-right-long"></i>
                  <div>
                    <span className="cargo-tag-label">TO</span>
                    <p>Dubai, UAE</p>
                  </div>
                </div>
                <div className="cargo-tag-status">
                  <span className="status-dot"></span> In Transit
                </div>
              </div>
              <div className="route-line">
                <i className="fas fa-truck-fast route-icon"></i>
              </div>
            </div>
          </div>
        </section>

        <section className="section banner-section" id="banners">
          <div
            className="container"
            style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}
          >
            <img
              src="/images/banner1.jpg"
              alt="Promotional Banner 1"
              style={{
                flex: "1 1 45%",
                minWidth: "280px",
                height: "260px",
                objectFit: "cover",
                borderRadius: "12px",
              }}
            />
            <img
              src="/images/banner2.jpg"
              alt="Promotional Banner 2"
              style={{
                flex: "1 1 45%",
                minWidth: "280px",
                height: "260px",
                objectFit: "cover",
                borderRadius: "12px",
              }}
            />
          </div>
        </section>

        <section className="section how-it-works" id="how-it-works">
          <div className="container">
            <div className="section-header">
              <h2>
                How It <span className="accent">Works</span>
              </h2>
              <p>From pickup to doorstep — four simple steps.</p>
            </div>
            <div className="steps-route">
              {STEPS_DATA.map((step, index) => (
                <div className="step" key={index}>
                  <span className="step-num">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div className="step-icon">
                    <i className={`fas ${step.icon}`}></i>
                  </div>
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section section-alt" id="services">
          <div className="container">
            <div className="section-header">
              <h2>
                Our <span className="accent">Services</span>
              </h2>
              <p>
                End-to-end logistics solutions built for individuals and
                businesses alike.
              </p>
            </div>
            <div className="courses-grid">
              {SERVICES_DATA.map((service, index) => (
                <article className="course-card" key={index}>
                  <div className="course-icon">
                    <i className={`fas ${service.icon}`}></i>
                  </div>
                  <h3>{service.title}</h3>
                  <p>{service.desc}</p>
                  <a href="#contact" className="btn btn-sm btn-secondary">
                    Get a Quote
                  </a>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section" id="about">
          <div className="container about-grid">
            <div className="about-image">
              <div className="about-illustration glass">
                <i className="fas fa-truck-ramp-box"></i>
              </div>
            </div>
            <div className="about-content">
              <h2>
                About <span className="accent">CombinedCourier</span>
              </h2>
              <p className="about-mission">
                We are a professional courier and logistics company committed to
                delivering your packages safely, quickly, and transparently,
                backed by real-time tracking every step of the way.
              </p>
              <h3>Why Choose Us</h3>
              <ul className="about-list">
                <li>
                  <i className="fas fa-check-circle"></i>10+ Years in Logistics
                  & Delivery
                </li>
                <li>
                  <i className="fas fa-check-circle"></i>Pan-India & Global
                  Delivery Network
                </li>
                <li>
                  <i className="fas fa-check-circle"></i>Live Shipment Tracking
                </li>
                <li>
                  <i className="fas fa-check-circle"></i>Secure Handling &
                  On-Time Delivery
                </li>
              </ul>
            </div>
          </div>
        </section>

        <section className="section section-alt" id="gallery">
          <div className="container">
            <div className="section-header">
              <h2>
                Our <span className="accent">Gallery</span>
              </h2>
              <p>A glimpse of our fleet, facilities, and team in action.</p>
            </div>
            <ImageCarousel images={GALLERY_IMAGES} />
          </div>
        </section>

        <section className="section" id="testimonials">
          <div className="container">
            <div className="section-header">
              <h2>
                What Our <span className="accent">Clients Say</span>
              </h2>
            </div>
            <div className="testimonial-slider">
              {TESTIMONIALS_DATA.map((testimonial, index) => (
                <article
                  className={`testimonial-card ${index === activeTestimonial ? "active" : "hidden"}`}
                  key={index}
                >
                  <div className="testimonial-header">
                    <div className="testimonial-photo">
                      {testimonial.initial}
                    </div>
                    <div>
                      <h3>{testimonial.name}</h3>
                      <p>{testimonial.program}</p>
                    </div>
                  </div>
                  <div className="testimonial-rating">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="fas fa-star"></i>
                    ))}
                  </div>
                  <p className="testimonial-text">{testimonial.text}</p>
                </article>
              ))}
              <div className="testimonial-controls">
                <button
                  onClick={prevTestimonial}
                  aria-label="Previous testimonial"
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
                <button onClick={nextTestimonial} aria-label="Next testimonial">
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="section section-alt" id="faq">
          <div className="container">
            <div className="section-header">
              <h2>
                Frequently Asked <span className="accent">Questions.....</span>
              </h2>
            </div>
            <div className="faq-accordion">
              {FAQS_DATA.map((faq, index) => (
                <div className="faq-item" key={index}>
                  <button
                    className="faq-question"
                    onClick={() => toggleFaq(index)}
                  >
                    {faq.q}
                    <span className="faq-icon">
                      <i
                        className={`fas fa-${activeFaq === index ? "minus" : "plus"}`}
                      ></i>
                    </span>
                  </button>
                  <div
                    className={`faq-answer ${activeFaq === index ? "open" : ""}`}
                  >
                    <p>{faq.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section" id="contact">
          <div className="container contact-grid">
            <div className="contact-form-wrapper">
              <h2>
                Contact <span className="accent">Us</span>
              </h2>
              <form onSubmit={handleContactSubmit}>
                <div className="form-group">
                  <label htmlFor="fullName">Full Name</label>
                  <input
                    type="text"
                    id="fullName"
                    name="Name"
                    required
                    placeholder="Enter your full name"
                    value={contactForm.Name}
                    onChange={handleContactChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="Email"
                    required
                    placeholder="Enter your email address"
                    value={contactForm.Email}
                    onChange={handleContactChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="Phone"
                    placeholder="Enter your phone number"
                    value={contactForm.Phone}
                    onChange={handleContactChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="Message"
                    rows="4"
                    required
                    placeholder="How can we help you?"
                    value={contactForm.Message}
                    onChange={handleContactChange}
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={contactLoading}
                >
                  {contactLoading ? "Sending..." : "Submit Message"}
                </button>
                {contactStatus === "success" && (
                  <p className="contact-success">Message sent successfully!</p>
                )}
                {contactStatus === "error" && (
                  <p className="contact-error">
                    Something went wrong. Try again.
                  </p>
                )}
              </form>
            </div>

            <div className="contact-map-wrapper">
              <iframe
                title="Our Location"
                src="https://www.google.com/maps?q=9-10,+Ground+Floor,+Ashram+Rd,+opposite+Times+of+India,+Muslim+Society,+Navrangpura,+Ahmedabad,+Gujarat+380009,+India&output=embed"
                width="100%"
                height="350"
                style={{ border: 0, borderRadius: "10px" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </section>
      </main>

      <footer className="footer" id="footer">
        <div className="container footer-grid">
          <div className="footer-col">
            <h3>CombinedCourier</h3>
            <p>Fast, reliable, and secure courier & logistics services.</p>
          </div>
          <div className="footer-col">
            <h4>Follow Us</h4>
            <div className="footer-social">
              <a
                href="https://www.facebook.com/combinedcourier"
                data-social="facebook"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <i className="fab fa-facebook-f"></i>
              </a>
              <a
                href="#"
                data-social="instagram"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <i className="fab fa-instagram"></i>
              </a>
              <a
                href="#"
                data-social="twitter"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Twitter"
              >
                <i className="fab fa-twitter"></i>
              </a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2026 CombinedCourier. All Rights Reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
